// ---------------------------------------------------------------------------
// domains.ts — the two-hostname split (§13.0 step 25)
//
// ReCurr is ONE React bundle, built once and served by one nginx, answering on
// two public hostnames:
//
//   recurr.co.za      — the MARKETING site. The page a visitor lands on, and
//                       the page MailerSend's reviewers open.
//   app.recurr.co.za  — the PLATFORM. Login, password reset, signup, the
//                       dashboard, hosted checkout — every screen that does
//                       something rather than describes something.
//
// Because it is one bundle, the split cannot be a build flag: the same
// JavaScript has to behave differently depending on the host it was fetched
// from. That decision lives HERE and nowhere else — every redirect and every
// cross-host link in the app is derived from these three functions, so the two
// halves of the split can never disagree about which host owns a path.
//
// THE THIRD CASE IS THE ONE THAT MATTERS. Any hostname that is neither of the
// two above is 'unified': localhost, dev.recurr.co.za, the bare VPS IP, and —
// critically — every merchant custom domain and merchant subdomain. On those,
// this module does NOTHING. A merchant storefront on shop.acme.co.za must keep
// serving /store, /p/:id and /pay/:token from its own hostname; redirecting any
// of that to app.recurr.co.za would take a customer off the merchant's brand
// mid-checkout. 'unified' is therefore the safe default, and the two platform
// hostnames are matched EXACTLY rather than by suffix: `acme.recurr.co.za` is a
// merchant, not the platform.
// ---------------------------------------------------------------------------

export const MARKETING_HOST = 'recurr.co.za';
export const APP_HOST = 'app.recurr.co.za';

/** Which of the two public sites (if either) this hostname is serving. */
export type HostRole = 'marketing' | 'app' | 'unified';

/** Which site a given path belongs to. */
export type Surface = 'marketing' | 'platform' | 'shared';

const MARKETING_HOSTNAMES = new Set([MARKETING_HOST, `www.${MARKETING_HOST}`]);

/**
 * Paths served on BOTH hostnames, and never redirected between them.
 *
 * - /pricing is reached from the marketing site AND from the "Upgrade" CTA on
 *   a locked feature inside the dashboard; the page already adapts its CTA to
 *   whether a merchant is logged in, so it has to render in both places.
 * - /legal* and /status are linked from the marketing footer, from merchant
 *   storefront footers and from the signup flow. Terms a visitor is asked to
 *   accept must open where they are standing.
 *
 * Search engines see these twice, which is what the platform host's own
 * robots.txt (frontend/public/robots-app.txt) is for.
 */
const SHARED_PREFIXES = ['/pricing', '/legal', '/status'];

/**
 * Paths served ONLY on the marketing hostname, beside `/`.
 *
 * ⚠ THIS LIST IS THE FIRST OF THE FIVE EDITS A NEW PUBLIC URL NEEDS, AND IT IS
 * THE ONE WHOSE ABSENCE IS SILENT. Everything not matched here or above is
 * classified 'platform', and `resolveHostRedirect` then bounces it from
 * recurr.co.za to app.recurr.co.za — so an /about wired into App.tsx alone
 * compiles, renders in development, passes its unit tests, and sends every
 * real visitor to the login screen. The other four edits are the route in
 * App.tsx, a `PageSeo` entry AND a `SITEMAP_PATHS` entry in config/seo.ts, a
 * check that public/robots.txt does not Disallow the prefix, and the footer
 * link (M3's three pages are linked from the FOOTER and from body copy, never
 * the top nav — the header row was measured at 920px and three more items
 * would undo it; `/blog` is the one exception, by the founder's instruction,
 * and the row was re-measured to 969px for it, then to 854px when "See it
 * working" left it the same day — index.css; since then /blog is in the footer
 * too).
 *
 * These are NOT 'shared'. A shared path renders on both hostnames because both
 * link to it; /about, /security, /developers and /wordpress-plugin are pages a
 * visitor seeks out before signing up, so on the app host they behave like `/`
 * and hand the visitor the login screen instead. (/wordpress-plugin is A10's
 * plugin-or-platform comparison, 2026-09-14 — and the one page here with no
 * footer link, by the founder's ruling: the home page and /about link it.)
 */
// `/blog` covers /blog and every /blog/<slug> beneath it (the match is on a
// segment boundary, so it covers nothing else). C1 / M5 part 1, 2026-10-10 —
// the one marketing path with a top-nav item, by the founder's instruction;
// the per-article half of the plumbing is derived from config/blog.ts.
// `/platform-account` (B131, 2026-10-25): the platform account announced before
// it is sellable — reached from the block under the plan cards on / and
// /pricing, not from the header or the footer.
const MARKETING_PREFIXES = ['/about', '/security', '/developers', '/wordpress-plugin', '/blog', '/platform-account'];

/** Prefix match on a SEGMENT boundary — "/statusboard" is not "/status". */
function underPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function currentHostname(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname;
}

/** This origin, falling back to the platform host when there is no window. */
function currentOrigin(): string {
  return typeof window === 'undefined' ? `https://${APP_HOST}` : window.location.origin;
}

/**
 * Classify a hostname. Anything not matched exactly is 'unified' — see the
 * header: that is the case that keeps merchant domains working untouched.
 */
export function hostRole(hostname: string = currentHostname()): HostRole {
  const lc = hostname.toLowerCase();
  if (lc === APP_HOST) return 'app';
  if (MARKETING_HOSTNAMES.has(lc)) return 'marketing';
  return 'unified';
}

/** Classify a path. Everything not marketing or shared is platform. */
export function surfaceForPath(pathname: string): Surface {
  if (pathname === '/') return 'marketing';

  for (const prefix of SHARED_PREFIXES) {
    if (underPrefix(pathname, prefix)) return 'shared';
  }

  for (const prefix of MARKETING_PREFIXES) {
    if (underPrefix(pathname, prefix)) return 'marketing';
  }

  return 'platform';
}

/**
 * What this host should do with this path.
 *
 * 'external' leaves the origin (marketing → platform, full page load).
 * 'internal' stays put and moves within the SPA.
 * null means render it here, which is always the answer on a 'unified' host.
 */
export type HostRedirect =
  | { kind: 'external'; url: string }
  | { kind: 'internal'; to: string }
  | null;

export function resolveHostRedirect(
  pathname: string,
  search = '',
  hash = '',
  hostname: string = currentHostname(),
): HostRedirect {
  const role = hostRole(hostname);
  if (role === 'unified') return null;

  const surface = surfaceForPath(pathname);
  if (surface === 'shared') return null;

  // The marketing site holds no platform pages. Query string and hash are
  // carried across so an emailed or SMS'd link — a password reset, a payment
  // link, a signup deep-linked to a plan — survives the move intact.
  if (role === 'marketing' && surface === 'platform') {
    return { kind: 'external', url: `https://${APP_HOST}${pathname}${search}${hash}` };
  }

  // The app host's front door is the login screen, not the sales pitch:
  // someone typing app.recurr.co.za wants in. LoginPage forwards an already
  // authenticated session on to the dashboard, so this costs a logged-in
  // merchant nothing.
  if (role === 'app' && surface === 'marketing') {
    return { kind: 'internal', to: '/login' };
  }

  return null;
}

/**
 * A link to a PLATFORM path. Absolute only when we are on the marketing host
 * and would otherwise be linking to a page this host does not serve; a plain
 * path everywhere else, so the SPA keeps navigating in-place on the app host
 * and on localhost.
 */
export function appUrl(path: string, hostname: string = currentHostname()): string {
  return hostRole(hostname) === 'marketing' ? `https://${APP_HOST}${path}` : path;
}

/** A link to a MARKETING path, on the same rule in the other direction. */
export function marketingUrl(path = '/', hostname: string = currentHostname()): string {
  return hostRole(hostname) === 'app' ? `https://${MARKETING_HOST}${path}` : path;
}

// ---------------------------------------------------------------------------
// The merchant's public address
// ---------------------------------------------------------------------------

/**
 * The customer signup link — THE ONE PUBLIC ADDRESS EVERY MERCHANT HAS.
 *
 * ⚠ THREE SURFACES USED TO PRINT A DIFFERENT ANSWER TO "WHERE DO MY CUSTOMERS
 * FIND ME", AND TWO OF THEM WERE PRINTING AN ADDRESS THAT 404s. Measured on
 * production 2026-09-03, on app.recurr.co.za:
 *
 *   /api/public/pages/<uuid>/homepage   → 404 STOREFRONT_NOT_FOUND
 *   /api/public/pages/rec10001/homepage → 404 STOREFRONT_NOT_FOUND
 *   /api/public/products                → 404 STOREFRONT_NOT_FOUND
 *   /api/health                         → 200   (control: the host is fine)
 *
 * `merchantResolver` resolves a merchant ONLY by hostname — custom domain,
 * then subdomain — and BOTH of those are the white-label feature withdrawn on
 * 2026-09-02. There is no path-parameter fallback, so on the platform host it
 * can never resolve, for anyone. `/store/*` therefore has no entrance at all
 * (BP-106, item B18 — STILL OPEN).
 *
 * So the word "storefront" was withdrawn from the merchant-facing surfaces:
 * there isn't one. THIS link is what survives, because it does not go through
 * merchantResolver at all — StandaloneSignupController resolves the ref itself
 * and accepts a display_code as readily as a UUID.
 *
 * ▶ PREFER THE DISPLAY CODE (`rec10001`). It is the same reference the
 *   dashboard tells the merchant to quote to support, and a merchant can read
 *   it down a phone. Pass the UUID only when the code is not loaded yet.
 *
 * ---------------------------------------------------------------------------
 * ✅ UPDATED 2026-09-04 — /p/:merchantId AND /p/:merchantId/<slug> NOW WORK
 * ---------------------------------------------------------------------------
 * Item B19 fixed the mount order (BP-105) and resolved the ref on the way in
 * (BP-108), so the Page Builder pages reach their own controller and BOTH ref
 * forms work. Re-measured against the real routers, a real Postgres and a real
 * merchant carrying a published page:
 *
 *   /api/public/pages/rec10001/homepage → 200   (was 404 STOREFRONT_NOT_FOUND)
 *   /api/public/pages/rec10001/<slug>   → 200
 *   /api/public/navigation/rec10001/main→ 200
 *   /api/public/pages/foo/homepage      → 404 MERCHANT_NOT_FOUND (was a 500)
 *   /api/public/products                → 404 STOREFRONT_NOT_FOUND (unchanged)
 *
 * ⚠ TWO THINGS THAT DID NOT CHANGE, AND COPY MUST NOT SAY OTHERWISE.
 *   1. THERE IS STILL NO STOREFRONT. B19 opened the PAGES, not the shop —
 *      the catalogue, cart and checkout are still B18 and still have no
 *      entrance. Do not describe /p/:id as a place to buy anything.
 *   2. NOTHING SEEDS A HOMEPAGE AT SIGNUP. `MerchantSignupService` creates a
 *      user and a subscription and no `pages` row, so /p/<ref> is an honest
 *      "Page not found" for every merchant who has never opened the Page
 *      Builder. It is a working route, not a working page.
 *
 * ✅ UPDATED 2026-09-09 — /p/:id/kb WORKS TOO. B20 closed on 2026-09-04 and is
 *   live: `/api/kb-public/rec10001/articles` answers 200 and a malformed ref
 *   answers 404 MERCHANT_NOT_FOUND. This block used to warn that it was a live
 *   500; that warning was true until B20 shipped and stale for five days after.
 *   Pages, navigation AND the knowledge base now share one ref resolver
 *   (`middleware/publicMerchantRefResolver.ts`).
 */
export function customerSignupUrl(ref: string, origin: string = currentOrigin()): string {
  return `${origin}/p/${ref}/signup`;
}

/**
 * ---------------------------------------------------------------------------
 * publicCtaHref — where a Page Builder block's button may send a visitor
 * (item B18, ruled 2026-09-09: the anonymous storefront stays withdrawn)
 * ---------------------------------------------------------------------------
 *
 * ⚠ THE PUBLIC STOREFRONT IS WITHDRAWN, AND THREE BLOCKS USED TO POINT AT IT.
 *
 * `/store/*` resolved a merchant by HOSTNAME only, and C5 withdrew every
 * hostname that could resolve on 2026-09-02, so the catalogue, the cart and the
 * checkout have no anonymous entrance. That was recorded as "no entrance",
 * which read as unreachable. Measured in a real browser against production on
 * 2026-09-09, it was the opposite of unreachable:
 *
 *   /store/products  200, renders a full branded storefront, one API call
 *                    (404 /api/public/products), then the words
 *                    "Failed to load products. Please try again."
 *   /store/cart      200, renders "Your cart is empty", ZERO API calls
 *
 * ▶ "Please try again" is a TRANSIENT message for a PERMANENT condition.
 *
 * And B19 made the Page Builder pages public on 2026-09-04, which armed it:
 * the hero, pricing and CTA blocks all navigated to `/store/products`, so a
 * merchant's own public page could carry buttons that land their customer on
 * a retry prompt that can never succeed.
 *
 * ⚠ THE PALETTE'S DEFAULTS ARE COPIED INTO THE SAVED PAGE (`addBlock` spreads
 *   `defaultProps`), so pages ALREADY stored in the database carry the dead
 *   path in their own JSON. Changing the palette alone would not reach them.
 *   ▶ THAT IS WHY THIS RESOLVES AT RENDER TIME AND NOT AT AUTHORING TIME —
 *   it is the only fix that reaches a page saved before today, without a
 *   migration that rewrites merchants' content.
 *
 * The rules, in order:
 *   - an absolute URL         → the merchant's own choice, returned unchanged
 *   - a withdrawn /store path → the merchant's customer signup link, which is
 *                               the one public address measured working
 *   - nothing at all          → the signup link when a ref is known
 *   - any other in-app path   → returned unchanged
 *
 * When no ref is known this returns '' rather than guessing, and every caller
 * renders no button for '' — a missing button is honest; a dead one is not.
 */
export function publicCtaHref(rawUrl: string | undefined, ref: string | undefined): string {
  const url = (rawUrl ?? '').trim();

  // A merchant's own external link is theirs. Never rewrite it.
  if (/^https?:\/\//i.test(url)) return url;

  // The withdrawn storefront, in every form a stored page might hold it:
  // "/store", "/store/products", "/store/products/x", with or without a query.
  const isWithdrawnStorePath = url === '/store' || url.startsWith('/store/');

  if (url === '' || isWithdrawnStorePath) {
    // ⚠ RELATIVE ON PURPOSE. `customerSignupUrl` defaults to an ABSOLUTE URL,
    // and every caller sends an absolute one to `window.open(…, '_blank')` —
    // so the default would open the merchant's own signup page in a NEW TAB.
    // Passing an empty origin keeps the single definition of this link while
    // producing the in-app path react-router can navigate.
    return ref ? customerSignupUrl(ref, '') : '';
  }

  return url;
}

/** True when a URL produced above leaves this origin, so it needs an <a>. */
export function isCrossHost(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Navigate to a platform path from anywhere, crossing hosts if required.
 * For the imperative cases (a plan card, a form submit) where there is no
 * anchor to hang a href on.
 */
export function navigateToApp(path: string, navigate: (to: string) => void): void {
  const url = appUrl(path);
  if (isCrossHost(url)) window.location.assign(url);
  else navigate(url);
}
